import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import {
  beginAssistantLogin,
  clearAssistantAuthState,
  ensureAssistantCsrfToken,
  getAssistantSession,
  type AuthAccount,
  logoutAssistant,
} from "@/auth/assistantAuth";
import { clearAllCaches } from "@/apollo/links/auth";

type AuthCallbackState = "idle";

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  authInitialized: boolean; // Track if auth check has completed at least once
  authError: string | null;
  authCallbackState: AuthCallbackState;
  authRedirectTarget: string | null;
  login: (redirectTo?: string) => Promise<void>;
  activeAccount: AuthAccount | null;
  // Token helpers
  getAccessToken: (scopes?: string[]) => Promise<string | null>;
  // Session / utility
  checkAuthStatus: () => Promise<void>;
  logout: () => Promise<void>;
  user?: { email?: string; givenName?: string; surname?: string } | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

const SIGN_IN_FAILED_MESSAGE = "Sign in was not successful. We couldn't verify your access to the Assistant API.";
const ACCESS_DENIED_RECOVERY_MESSAGE =
  "Sign in was not successful. Your account does not currently have access. Try the other sign-in option. If that still doesn't work, contact support.";

const isAssistantAccessDeniedError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  const normalizedMessage = error.message.trim().toLowerCase();
  return (
    normalizedMessage.includes("unauthorized") ||
    normalizedMessage.includes("forbidden") ||
    normalizedMessage.includes("access denied") ||
    normalizedMessage.includes("did not return your profile")
  );
};

const getAuthFailureMessage = (error: unknown): string => {
  if (!(error instanceof Error) || error.message.trim().length === 0) {
    return SIGN_IN_FAILED_MESSAGE;
  }

  const normalizedMessage = error.message.trim().toLowerCase();
  if (
    normalizedMessage.includes("unauthorized") ||
    normalizedMessage.includes("forbidden") ||
    normalizedMessage.includes("access denied") ||
    normalizedMessage.includes("did not return your profile")
  ) {
    return ACCESS_DENIED_RECOVERY_MESSAGE;
  }

  return `${SIGN_IN_FAILED_MESSAGE} ${error.message.trim()}`;
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authCallbackState] = useState<AuthCallbackState>("idle");
  const [authRedirectTarget, setAuthRedirectTarget] = useState<string | null>(null);
  const [user, setUser] = useState<{
    email?: string;
    givenName?: string;
    surname?: string;
  } | null>(null);
  const [activeAccount, setActiveAccount] = useState<AuthAccount | null>(null);

  useEffect(() => {
    if (import.meta.env.VITE_APPINSIGHTS_ENABLED !== "true") return;

    let disposed = false;

    void (async () => {
      const { appInsights } = await import("@/telemetry/ai");
      if (disposed) return;

      if (!activeAccount) {
        appInsights.clearAuthenticatedUserContext();
        return;
      }

      const userId = activeAccount.id;

      if (userId) {
        appInsights.setAuthenticatedUserContext(userId);
      } else {
        appInsights.clearAuthenticatedUserContext();
      }
    })();

    return () => {
      disposed = true;
    };
  }, [activeAccount]);

  const getAccessToken = useCallback(async (): Promise<string | null> => null, []);

  const syncSessionState = useCallback((account: AuthAccount | null) => {
    setIsAuthenticated(Boolean(account));
    setActiveAccount(account);
    setUser(
      account
        ? {
            email: account.email ?? account.username,
            givenName: account.givenName,
            surname: account.surname,
          }
        : null,
    );
  }, []);

  const rejectActiveAccount = useCallback(
    async (error: unknown): Promise<void> => {
      clearAssistantAuthState();
      syncSessionState(null);
      setAuthError(getAuthFailureMessage(error));
    },
    [syncSessionState],
  );

  const checkAuthStatus = useCallback(async (): Promise<void> => {
    try {
      const session = await getAssistantSession();
      if (session?.account) {
        await ensureAssistantCsrfToken();
        syncSessionState(session.account);
        setAuthError(null);
      } else {
        clearAssistantAuthState();
        syncSessionState(null);
        setAuthError(null);
      }
    } catch (error) {
      console.error("Auth status check failed:", error);
      if (isAssistantAccessDeniedError(error)) {
        await rejectActiveAccount(error);
        return;
      }

      clearAssistantAuthState();
      syncSessionState(null);
      setAuthError(getAuthFailureMessage(error));
    }
  }, [rejectActiveAccount, syncSessionState]);

  const login = useCallback(async (redirectTo?: string): Promise<void> => {
    setAuthError(null);
    setAuthRedirectTarget(redirectTo ?? null);
    await beginAssistantLogin({ redirectTo });
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      setIsLoading(true);
      try {
        await checkAuthStatus();
      } catch (e) {
        console.error("Auth bootstrap failed:", e);
        await rejectActiveAccount(e);
      } finally {
        if (!cancelled) {
          setAuthInitialized(true);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkAuthStatus, rejectActiveAccount]);

  const logout = async (): Promise<void> => {
    syncSessionState(null);
    setAuthError(null);
    setAuthRedirectTarget(null);
    clearAllCaches();
    await logoutAssistant({ redirectTo: `${window.location.origin}/logout/callback` });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        authInitialized,
        authError,
        authCallbackState,
        authRedirectTarget,
        login,
        activeAccount,
        getAccessToken,
        checkAuthStatus,
        logout,
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Add display name for React DevTools and Fast Refresh
AuthProvider.displayName = "AuthProvider";

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
